import React, { useEffect, useState, useRef, useContext, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { SocketContext } from "../context/SocketContext";
import { setupEncryption, initializeWebSocket, sendEncryptedMessage, decryptMessage } from "./chatUtils";
import { sendTypingStatus } from "./typingUtils";
import { getMessages } from "../api";
import MessageBubble from "./MessageBubble";
import DateSeparator from "./DateSeparator";
import { uploadEncryptedFile, generateThumbnail, getFileCategory, formatFileSize } from "./mediaUtils";


const MemoizedMessageList = React.memo(({ messages, myMobile, recipientName, setReplyTo, sharedKeyRef }) => (
  <>
    {messages.map((msg, i) => {
      const prevMsg = messages[i - 1];
      const currDate = msg.timestamp ? new Date(msg.timestamp) : null;
      const prevDate = prevMsg?.timestamp ? new Date(prevMsg.timestamp) : null;
      const showSeparator =
        currDate &&
        (!prevDate ||
          currDate.getFullYear() !== prevDate.getFullYear() ||
          currDate.getMonth() !== prevDate.getMonth() ||
          currDate.getDate() !== prevDate.getDate());
      // Use stable key that doesn't change when status updates
      const msgKey = msg.id || msg.localId || msg.timestamp;
      return (
        <React.Fragment key={msgKey}>
          {showSeparator && <DateSeparator timestamp={msg.timestamp} />}
          <MessageBubble
            msg={msg}
            isMine={msg.sender === myMobile || msg.sender === "You"}
            onReply={(msg) => setReplyTo({
              sender: msg.sender === "You" ? myMobile : msg.sender,
              message: msg.message,
              timestamp: msg.timestamp,
            })}
            recipientName={recipientName}
            sharedKeyRef={sharedKeyRef}
          />
        </React.Fragment>
      );
    })}
  </>
));

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { recipientMobile } = useParams();
  const recipientName = location.state?.recipientName || "Recipient";

  const [messages, setMessages] = useState([]);

  // State
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [recipientIsTyping, setRecipientIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);


  // Refs
  const socketRef = useContext(SocketContext);
  const scrollRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const identityPrivateKeyRef = useRef(null);
  const sharedKeyRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const fileInputRef = useRef(null);
  const oldestTimestampRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const canLoadOlderRef = useRef(false);
  const isLoadingOlderRef = useRef(false);


  // Auth
  const token = localStorage.getItem("token");
  let myMobile = null;
  try {
    myMobile = token ? jwtDecode(token).mobile : null;
  } catch (e) {
    myMobile = null;
  }


  // Constants
  const CHUNK_SIZE = 25;

  useEffect(() => {
    // Reset pagination and scroll guards when recipient changes or token changes
    setHasMoreMessages(true);
    setLoadingOlder(false);
    hasScrolledRef.current = false;
    canLoadOlderRef.current = false;
    oldestTimestampRef.current = null;
    setMessages([]);

    const initialize = async () => {
      try {
        await setupEncryption(recipientMobile, identityPrivateKeyRef, sharedKeyRef);

        // WebRTC call offer deduplication
        const recentOffers = new Set();
        const onSignal = (msgEvent) => {
          try {
            const raw = typeof msgEvent.data === "string" ? msgEvent.data : JSON.stringify(msgEvent.data);
            let data;
            const parsed = JSON.parse(raw);
            if (parsed?.data && typeof parsed.data === "string") {
              data = JSON.parse(parsed.data);
            } else {
              data = parsed;
            }
            if (data?.type === "call:offer" && data.to === myMobile) {
              if (!data.offer?.sdp) return;
              const offerKey = `${data.from}:${data.callType}:${data.offer.sdp.slice(0, 200)}`;
              if (recentOffers.has(offerKey)) return;
              recentOffers.add(offerKey);
              setTimeout(() => recentOffers.delete(offerKey), 5000);
              navigate(`/call/${data.from}`, {
                state: { callType: data.callType, isCaller: false, initialOffer: data.offer, caller: data.from },
              });
            }
          } catch {}
        };

        await initializeWebSocket(
          token,
          socketRef,
          sharedKeyRef,
          myMobile,
          recipientMobile,
          setIsConnected,
          setMessages,
          onSignal
        );

        // Load initial messages
        const res = await getMessages(myMobile, recipientMobile, undefined, CHUNK_SIZE);
        const history = await Promise.all(
          res.data.messages.map(async (msg) => {
            const decrypted = {
              sender: msg.sender === myMobile ? "You" : msg.sender,
              message: await decryptMessage(msg.iv, msg.message, sharedKeyRef),
              timestamp: msg.timestamp,
              status: msg.status || null,
              deliveredAt: msg.deliveredAt || null,
              seenAt: msg.seenAt || null,
              replyTo: msg.replyTo
                ? {
                    sender: msg.replyTo.sender === myMobile ? "You" : msg.replyTo.sender,
                    message: msg.replyTo.message,
                    timestamp: msg.replyTo.timestamp,
                  }
                : null,
            };
            
            // Add media metadata if present
            if (msg.mediaType && msg.mediaUrl) {
              decrypted.mediaType = msg.mediaType;
              decrypted.mediaUrl = msg.mediaUrl;
              decrypted.mediaKey = msg.mediaKey;
              decrypted.mediaSize = msg.mediaSize;
              if (msg.mediaThumbnail) {
                decrypted.mediaThumbnail = msg.mediaThumbnail;
              }
              if (msg.mediaDuration) {
                decrypted.mediaDuration = msg.mediaDuration;
              }
            }
            
            return decrypted;
          })
        );
        if (history.length > 0) {
          const sorted = history.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          oldestTimestampRef.current = sorted[0].timestamp;
          setMessages(sorted);
          setHasMoreMessages(sorted.length >= CHUNK_SIZE);
          
          // Mark all incoming messages as seen when chat opens
          setTimeout(() => {
            const unseenMessages = sorted.filter(
              (m) => m.sender !== "You" && m.sender !== myMobile && (!m.status || m.status !== "seen")
            );
            unseenMessages.forEach((msg) => {
              try {
                socketRef.current?.send(
                  JSON.stringify({
                    action: "receipt",
                    data: JSON.stringify({ type: "seen", to: msg.sender, timestamp: msg.timestamp }),
                  })
                );
              } catch (e) {
                console.warn("Failed to send seen receipt:", e);
              }
            });
          }, 500);
          
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollRef.current?.scrollIntoView({ behavior: "auto" });
              hasScrolledRef.current = true;
              canLoadOlderRef.current = true;
            });
          });
        }
      } catch {
        alert("Encryption setup failed. Please reload or try a different browser.");
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [token, recipientMobile]);

  // Mark messages as seen when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && socketRef.current?.readyState === WebSocket.OPEN) {
        // Mark all unseen incoming messages as seen
        const unseenMessages = messages.filter(
          (m) => m.sender !== "You" && m.sender !== myMobile && (!m.status || m.status !== "seen")
        );
        unseenMessages.forEach((msg) => {
          try {
            socketRef.current?.send(
              JSON.stringify({
                action: "receipt",
                data: JSON.stringify({ type: "seen", to: msg.sender, timestamp: msg.timestamp }),
              })
            );
          } catch (e) {
            console.warn("Failed to send seen receipt on visibility change:", e);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [messages, myMobile]);

    useEffect(() => {
      if (!hasScrolledRef.current && messages.length > 0) {
        // Double RAF to ensure layout settled before jumping to bottom
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollIntoView({ behavior: "auto" });
            hasScrolledRef.current = true;
            canLoadOlderRef.current = true; // enable top-loading only after initial bottom jump
          });
        });
      }
    }, [messages.length]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom =
        container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      setShowScrollButton(!isNearBottom);
      // Debug
      // console.debug("scroll", { top: container.scrollTop, isNearBottom, canLoadOlder: canLoadOlderRef.current });

      // Only allow loading older after initial scroll-to-bottom completed
      if (
        canLoadOlderRef.current &&
        container.scrollTop <= 10 &&
        hasMoreMessages &&
        !loadingOlder
      ) {
        console.log("🔼 Reached top, loading older messages...");
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [messages.length, hasMoreMessages, loadingOlder]);

  // Auto-stick to bottom when already near bottom and a new message arrives
  useEffect(() => {
    // Skip auto-scroll when loading older messages
    if (isLoadingOlderRef.current) {
      return;
    }
    
    const container = messagesContainerRef.current;
    if (!container) return;
    const isNearBottom =
      container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
    if (isNearBottom) {
      // Use auto to avoid jumpiness while typing
      scrollRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [messages.length]);

  // Removed IntersectionObserver to prevent unintended auto-loading on initial mount

  const loadOlderMessages = async () => {
    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      console.log("📦 Fetching older messages before:", oldestTimestampRef.current);
  const container = messagesContainerRef.current;
      const prevScrollHeight = container ? container.scrollHeight : null;
      if (!oldestTimestampRef.current) {
        setLoadingOlder(false);
        isLoadingOlderRef.current = false;
        return;
      }
  const res = await getMessages(myMobile, recipientMobile, oldestTimestampRef.current, CHUNK_SIZE);
      const older = await Promise.all(
        res.data.messages.map(async (msg) => ({
          sender: msg.sender === myMobile ? "You" : msg.sender,
          message: await decryptMessage(msg.iv, msg.message, sharedKeyRef),
          timestamp: msg.timestamp,
          status: msg.status || null,
          deliveredAt: msg.deliveredAt || null,
          seenAt: msg.seenAt || null,
          replyTo: msg.replyTo
            ? {
                sender: msg.replyTo.sender === myMobile ? "You" : msg.replyTo.sender,
                message: msg.replyTo.message,
                timestamp: msg.replyTo.timestamp,
              }
            : null,
        }))
      );

      if (older.length === 0) {
        setHasMoreMessages(false);
      } else {
        const sorted = older.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        oldestTimestampRef.current = sorted[0].timestamp;
        
        // Mark older unseen messages as seen
        if (document.visibilityState === 'visible' && socketRef.current?.readyState === WebSocket.OPEN) {
          const unseenOlderMessages = sorted.filter(
            (m) => m.sender !== "You" && m.sender !== myMobile && (!m.status || m.status !== "seen")
          );
          unseenOlderMessages.forEach((msg) => {
            try {
              socketRef.current?.send(
                JSON.stringify({
                  action: "receipt",
                  data: JSON.stringify({ type: "seen", to: msg.sender, timestamp: msg.timestamp }),
                })
              );
            } catch (e) {
              console.warn("Failed to send seen receipt for older message:", e);
            }
          });
        }
        
        // Prepend older messages and preserve scroll position
        setMessages((prev) => {
          const newMessages = [...sorted, ...prev];
          return newMessages;
        });

        // After DOM updates, adjust scroll so user stays at the same spot
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!container || prevScrollHeight == null) {
              isLoadingOlderRef.current = false;
              return;
            }
            const newScrollHeight = container.scrollHeight;
            // Set scrollTop so the content doesn't jump (keep same viewed messages)
            container.scrollTop = newScrollHeight - prevScrollHeight;
            
            // Reset the flag after scroll adjustment
            setTimeout(() => {
              isLoadingOlderRef.current = false;
            }, 50);
          });
        });
      }
    } catch (err) {
      console.error("❌ Failed to load older messages:", err);
      isLoadingOlderRef.current = false;
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleTypingStatus = useCallback((event) => {
    if (event.detail.from === recipientMobile) {
      setRecipientIsTyping(event.detail.isTyping);
    }
  }, [recipientMobile]);

  useEffect(() => {
    window.addEventListener("typing-status", handleTypingStatus);
    return () => window.removeEventListener("typing-status", handleTypingStatus);
  }, [handleTypingStatus]);

  useEffect(() => {
    // Clear any previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (input.length > 0) {
      // Send "typing: true" immediately when user types
      if (!isTyping) {
        setIsTyping(true);
        sendTypingStatus(socketRef, recipientMobile, true);
      }

      // Schedule "typing: false" after 1000ms of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        sendTypingStatus(socketRef, recipientMobile, false);
      }, 1000);
    } else {
      // If input cleared, ensure typing=false is sent
      if (isTyping) {
        setIsTyping(false);
        sendTypingStatus(socketRef, recipientMobile, false);
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [input, isTyping, recipientMobile]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert("File size must be less than 100MB");
      return;
    }

    setSelectedFile(file);

    // Generate preview for images and videos
    const category = getFileCategory(file.type);
    if (category === "image" || category === "video") {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };  // Clear file selection
  const clearFileSelection = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Send file message
  const handleSendFile = async () => {
    console.log("handleSendFile called, selectedFile:", selectedFile);
    if (!selectedFile) return;

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      alert("Socket not connected.");
      return;
    }

    if (!sharedKeyRef.current) {
      alert("Encryption not ready.");
      return;
    }

    setUploadingFile(true);
    console.log("Starting file upload...");

    try {
      // Upload encrypted file and get metadata
      const { s3Key, encryptedKey, thumbnail, duration } = await uploadEncryptedFile(
        selectedFile,
        sharedKeyRef.current
      );
      console.log("File uploaded successfully:", { s3Key, encryptedKey });

      const category = getFileCategory(selectedFile.type);

      // Prepare media metadata
      const mediaMetadata = {
        mediaType: category,
        mediaUrl: s3Key,
        mediaKey: encryptedKey,
        mediaSize: selectedFile.size,
      };

      if (thumbnail) {
        mediaMetadata.mediaThumbnail = thumbnail;
      }

      if (duration) {
        mediaMetadata.mediaDuration = duration;
      }

      const normalizedReplyTo = replyTo
        ? {
            sender: replyTo.sender === "You" ? myMobile : replyTo.sender,
            message: replyTo.message,
            timestamp: replyTo.timestamp,
          }
        : null;

      // Send media message (with optional caption from input)
      const caption = input.trim() || selectedFile.name;
      console.log("Sending media message with metadata:", mediaMetadata);
      const { localId, timestamp } = await sendEncryptedMessage(
        caption,
        myMobile,
        recipientMobile,
        sharedKeyRef,
        socketRef,
        normalizedReplyTo,
        mediaMetadata
      );
      console.log("Media message sent successfully");

      const newMessage = {
        sender: myMobile,
        message: caption,
        timestamp,
        localId,
        status: "sent",
        replyTo: normalizedReplyTo
          ? {
              sender: normalizedReplyTo.sender,
              message: normalizedReplyTo.message,
              timestamp: normalizedReplyTo.timestamp,
            }
          : null,
        ...mediaMetadata,
      };

      setMessages((prev) => [...prev, newMessage]);
      setReplyTo(null);
      setInput("");
      clearFileSelection();

      requestAnimationFrame(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    } catch (error) {
      console.error("File upload failed:", error);
      alert("Failed to send file. Please try again.");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      alert("Socket not connected.");
      return;
    }

    try {
      const normalizedReplyTo = replyTo
        ? {
            sender: replyTo.sender === "You" ? myMobile : replyTo.sender,
            message: replyTo.message,
            timestamp: replyTo.timestamp,
          }
        : null;

      const { localId, timestamp } = await sendEncryptedMessage(
        input,
        myMobile,
        recipientMobile,
        sharedKeyRef,
        socketRef,
        normalizedReplyTo
      );

      const newMessage = {
        sender: myMobile,
        message: input,
        timestamp,
        localId,
        status: "sent",
        replyTo: normalizedReplyTo
          ? {
              sender: normalizedReplyTo.sender,
              message: normalizedReplyTo.message,
              timestamp: normalizedReplyTo.timestamp,
            }
          : null,
      };

      setMessages((prev) => [...prev, newMessage]);
      setReplyTo(null);
      setInput("");

      requestAnimationFrame(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    } catch {
      alert("Encryption failed. Try again.");
    }
  };
    const initiateCall = (callType) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      alert("Socket not connected.");
      return;
    }

    socketRef.current.send(
      JSON.stringify({
        action: "signal",
        data: JSON.stringify({
          type: "call:offer",
          callType,
          from: myMobile,
          to: recipientMobile,
        }),
      })
    );

    navigate(`/call/${recipientMobile}`, {
      state: { callType, isCaller: true },
    });
  };

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Setting up secure chat...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid d-flex flex-column min-vh-100 position-relative p-3">
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between mb-3 position-sticky top-0 bg-body"
        style={{ zIndex: 1020 }}
      >
        <div className="d-flex align-items-center">
          <i
            className="bi bi-arrow-left text-secondary fs-5 me-2"
            role="button"
            onClick={() => navigate("/users")}
            title="Back to Contacts"
          />
          <div className="d-flex align-items-center">
            {location.state?.recipientAvatar ? (
              <img
                src={location.state.recipientAvatar}
                alt="Avatar"
                className="rounded-circle me-3"
                style={{ width: "3rem", height: "3rem", objectFit: "cover" }}
              />
            ) : (
              <div
                className="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center me-3 fs-5"
                style={{ width: "3rem", height: "3rem" }}
              >
                {recipientName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="fw-bold">{recipientName}</div>
              <small className="text-muted">{recipientMobile}</small>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <i
            className="bi bi-telephone-fill text-success fs-5"
            role="button"
            title="Audio Call"
            onClick={() => initiateCall("audio")}
          />
          <i
            className="bi bi-camera-video-fill text-primary fs-5"
            role="button"
            title="Video Call"
            onClick={() => initiateCall("video")}
          />
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-grow-1 overflow-auto position-relative"
        style={{ minHeight: 0 }}
      >
        {/* Load older button/spinner */}
        {(hasMoreMessages || loadingOlder) && (
          <div className="d-flex justify-content-center mb-2">
            {loadingOlder ? (
              <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            ) : (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={loadOlderMessages}
                disabled={loadingOlder}
              >
                Load More
              </button>
            )}
          </div>
        )}
        {/* Messages list */}
        <MemoizedMessageList
          messages={messages}
          myMobile={myMobile}
          recipientName={recipientName}
          setReplyTo={setReplyTo}
          sharedKeyRef={sharedKeyRef}
        />
        {/* Typing Indicator - left side, just above scrollRef */}
        {recipientIsTyping && (
          <div
            className="position-sticky"
            style={{
              bottom: 0,
              margin: '0.5rem 0.75rem',
              background: 'rgba(255,255,255,0.85)',
              borderRadius: '8px',
              padding: '4px 10px',
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="fst-italic text-muted small">{recipientName}</span>
            <span className="d-inline-flex align-items-center gap-1" aria-hidden="true">
              <span
                className="spinner-grow text-secondary"
                style={{ '--bs-spinner-width': '0.5rem', '--bs-spinner-height': '0.5rem', animationDelay: '0s' }}
              />
              <span
                className="spinner-grow text-secondary"
                style={{ '--bs-spinner-width': '0.5rem', '--bs-spinner-height': '0.5rem', animationDelay: '0.2s' }}
              />
              <span
                className="spinner-grow text-secondary"
                style={{ '--bs-spinner-width': '0.5rem', '--bs-spinner-height': '0.5rem', animationDelay: '0.4s' }}
              />
            </span>
            <span className="visually-hidden">is typing</span>
          </div>
        )}
        <div ref={scrollRef} />
      </div>      {/* Jump to Bottom Button */}
      {showScrollButton && (
        <button
          className="btn btn-light shadow position-fixed rounded-circle p-0 d-flex align-items-center justify-content-center"
          style={{ bottom: 100, right: 20, zIndex: 1000, width: 48, height: 48 }}
          onClick={scrollToBottom}
          title="Jump to Bottom"
        >
          <i className="bi bi-arrow-down-short fs-4"></i>
        </button>
      )}

      {/* Reply Preview */}
      {replyTo && (
        <div className="px-2 py-2 d-flex align-items-stretch bg-light">
          <div className="border-start border-4 border-primary flex-grow-1 d-flex">
            <div className="ms-2 flex-grow-1">
              <div className="text-primary fw-semibold">
                {replyTo.sender === myMobile ? "You" : recipientName}
              </div>
              <div className="text-truncate text-secondary small">
                {replyTo.message}
              </div>
            </div>
            <button
              className="btn btn-link text-muted px-2"
              onClick={() => setReplyTo(null)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        </div>
      )}

      {/* File Preview */}
      {selectedFile && (
        <div className="px-2 py-2 bg-light border-top">
          <div className="d-flex align-items-center gap-2">
            {filePreview ? (
              <img 
                src={filePreview} 
                alt="Preview" 
                className="rounded"
                style={{ width: 60, height: 60, objectFit: 'cover' }}
              />
            ) : (
              <div 
                className="bg-secondary rounded d-flex align-items-center justify-content-center"
                style={{ width: 60, height: 60 }}
              >
                <i className="bi bi-file-earmark text-white fs-3"></i>
              </div>
            )}
            <div className="flex-grow-1">
              <div className="fw-semibold text-truncate" style={{ maxWidth: 200 }}>
                {selectedFile.name}
              </div>
              <div className="small text-muted">
                {formatFileSize(selectedFile.size)}
              </div>
            </div>
            <button
              className="btn btn-link text-danger px-2"
              onClick={clearFileSelection}
              disabled={uploadingFile}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* Input (sticky at bottom to avoid hiding) */}
      <div className="d-flex align-items-center gap-2 position-sticky bottom-0 bg-body pt-2">
        <span
          className={`fw-bold fs-4 ${isConnected ? "text-success" : "text-danger"}`}
          title={isConnected ? "Connected" : "Disconnected"}
          style={{ lineHeight: 1 }}
        >
          |
        </span>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {/* Attachment button */}
        <button
          className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
          disabled={uploadingFile}
          style={{ width: 40, height: 40 }}
        >
          <i className="bi bi-paperclip fs-5"></i>
        </button>
        
        <input
          className="form-control"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={selectedFile ? "Add a caption (optional)" : "Message"}
          disabled={uploadingFile}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (selectedFile) {
                handleSendFile();
              } else {
                handleSend();
              }
            }
          }}
        />
        
        <button
          className="btn btn-primary d-flex align-items-center justify-content-center"
          onClick={selectedFile ? handleSendFile : handleSend}
          title="Send"
          disabled={uploadingFile || (!selectedFile && !input.trim())}
          style={{ width: 40, height: 40 }}
        >
          {uploadingFile ? (
            <span className="spinner-border spinner-border-sm"></span>
          ) : (
            <i className="bi bi-send-fill fs-5"></i>
          )}
        </button>
      </div>
    </div>
  );
}